/**
 * SidebarNav — persistenter Desktop-Sidebar (≥lg), M-UX-3.1
 * (`plan-ux-3.1-sidebar.md` §1). Rendert dieselben `midSections` wie das
 * heutige Top-Bar-Mittel-Band, aber als vertikale, permanent aufgeklappte
 * Liste statt Dropdown-Buttons — Nordstern: alle Sektionen ohne Klick
 * sichtbar (lexoffice/sevdesk-Marktkonvention) + permanente
 * Feature-/Upgrade-Sichtbarkeit.
 *
 * Reiner Presentational-Renderer: baut KEINEN `NavContext` auf, ruft KEINE
 * Firebase-/Firestore-Hooks (nur `useTranslation`/`useLocation`, analog zu
 * {@link NavItemLink}). `sections`/`ctx`/`isPremium` kommen vollständig als
 * Props aus `MainLayout`, wo die zugrunde liegenden Hooks bereits vorliegen.
 *
 * `NavItemLink` wird 1:1 wiederverwendet (§1.5 Begründung: visibility-
 * Auflösung, `locked`-Rendering und `data-nav-id` liegen dort an einer
 * Stelle — ein sidebar-eigenes Item-Rendering würde das duplizieren).
 * `DesktopNavDropdown` wird bewusst NICHT wiederverwendet — der Sidebar ist
 * kein Dropdown, Sektionen sind immer offen (kein `isOpen`-State hier).
 *
 * @module components/layout/SidebarNav
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconBilling } from '../../icons';
import { NAV_SECTION_ICONS, type NavContext, type NavSection } from '../../config/navigation';
import { NavItemLink } from './NavTreeMenus';

interface SidebarNavProps {
  /** Bereits rollen-/hidden-gefilterte Sektionen (`midSections` aus MainLayout) — behält `locked`. */
  sections: NavSection[];
  ctx: NavContext;
  /** Steuert, ob die Upgrade-Karte im Fuß gerendert wird. */
  isPremium: boolean;
  /** Responsiv-/Layout-Klassen vom Layout (Kollaps `<lg`, sticky, Breite). */
  className?: string;
}

/**
 * Statischer Desktop-Upgrade-Anker im Sidebar-Fuß (§1.3). Nur bei
 * `!isPremium` gerendert — reiner Link auf `/dashboard/abonnement`, KEIN
 * Stripe-Portal-Call (der bleibt im Avatar-Menü der Top-Bar,
 * `handleManageSubscription`).
 *
 * Text-Korrektur 2026-07-29 (Ultra-Upsell-Konsistenz): der bisherige Wortlaut
 * („Alle Premium-Funktionen freischalten – Bank-Abgleich, Belegscanner & mehr")
 * ordnete Bank-Abgleich und Belegscanner dem PREMIUM-Tarif zu, obwohl beide
 * Ultra-exklusiv sind (`bankReconcileAccess`/`receiptScanAccess`:
 * `tier === 'ultra'`) — eine irreführende Leistungsangabe (§5 UWG). Der neue
 * Text nennt je Tarif nur Funktionen, die dieser Tarif auch wirklich enthält.
 * Die Sichtbarkeits-Logik der Karte bleibt unverändert (`!isPremium`).
 *
 * Jurist-MINOR M-1 (Re-Loop 1, plan-feedback-jurist-ultra-upsell.md §3.2): die
 * ÜBERSCHRIFT derselben Karte lautete weiterhin „Premium abonnieren"
 * (`subscribePremium`) — innerhalb EINES klickbaren Elements stand damit eine
 * Premium-Handlungsaufforderung direkt über einer Liste mit Ultra-exklusiven
 * Funktionen. Sie nutzt jetzt den tarifneutralen Key `upgradeCardHeading`.
 * `subscribePremium` bleibt UNVERÄNDERT — der Key wird an zwei weiteren, dort
 * fachlich korrekten Stellen verwendet (`MainLayout.tsx` Avatar-Menü,
 * `config/navigation.ts` `konto`-Sektion; per Grep verifiziert).
 */
const UpgradeCard: React.FC = () => {
  const { t } = useTranslation('navigation');
  return (
    <Link
      to="/dashboard/abonnement"
      data-nav-id="sidebar-upgrade-card"
      className="mt-auto mx-3 mb-3 flex flex-col rounded-lg bg-indigo-600 px-3 py-3 text-[#0C1322]
                 transition hover:bg-indigo-500"
    >
      <span className="flex items-center text-sm font-semibold">
        <span className="mr-2">
          <IconBilling />
        </span>
        {t('upgradeCardHeading', { defaultValue: 'Tarif upgraden' })}
      </span>
      <span className="mt-1 text-xs text-[#0C1322] text-opacity-80">
        {t('upgradeCardBody', {
          defaultValue:
            'Premium: unbegrenzt Rechnungen, Mahnwesen & offene Posten. Ultra: zusätzlich Bank-Abgleich, DATEV & Reisekosten.',
        })}
      </span>
    </Link>
  );
};

/**
 * Persistenter Sidebar (§1.2): pro Sektion eine statische Überschrift
 * (`<h2>`, Icon + Label) + die zugehörigen Items. KEIN Auf-/Zuklappen — das
 * ist der Kern des persistenten Layouts. `locked`-Items werden von
 * {@link NavItemLink} bereits als Link + Schloss-Icon gerendert und
 * navigieren normal zur Route (dort greift ggf. ein Feature-Teaser,
 * s. §1.2 Kommentar zu `ReceiptScanPage`) — die Sidebar dupliziert diese
 * Logik nicht.
 */
const SidebarNav: React.FC<SidebarNavProps> = ({ sections, ctx, isPremium, className }) => {
  const { t } = useTranslation('navigation');

  return (
    <nav
      aria-label={t('primaryNavigation', { defaultValue: 'Hauptnavigation' })}
      className={className}
    >
      {/* fix/ux-shell-scroll: fb-thin-scrollbar ersetzt die klobige Standard-
          Scrollleiste durch eine dezente Leiste (plan-ux-shell-scroll.md §2/§5) */}
      <div className="flex-1 overflow-y-auto fb-thin-scrollbar py-4 space-y-6">
        {sections.map((section) => {
          const Icon = NAV_SECTION_ICONS[section.id];
          const headingId = `sidebar-sec-${section.id}`;
          // M-UX-Cockpit-Home (§D.2): `chromeless`-Sektionen (z. B. „Übersicht")
          // rendern KEINE `<h2>`-Überschrift — sie würde ein gleichnamiges
          // Ein-Item nur doppeln. Ohne Überschrift entfällt auch das
          // `aria-labelledby` (kein toter ID-Verweis); die Items bleiben über
          // ihr eigenes Label zugänglich.
          return (
            <section
              key={section.id}
              aria-labelledby={section.chromeless ? undefined : headingId}
              className="px-3"
            >
              {!section.chromeless && (
                <h2
                  id={headingId}
                  className="flex items-center px-2 mb-1 text-xs font-semibold uppercase tracking-wide text-faint"
                >
                  {Icon && (
                    <span className="mr-2" aria-hidden="true">
                      <Icon />
                    </span>
                  )}
                  {t(section.labelKey, { ns: 'navigation' })}
                </h2>
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavItemLink key={item.id} item={item} ctx={ctx} variant="sidebar" highlightActive />
                ))}
              </div>
            </section>
          );
        })}
      </div>
      {!isPremium && <UpgradeCard />}
    </nav>
  );
};

export default SidebarNav;
